import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { fetchPageContent } from '../../utils/pageContentStorage';
import { useSEO } from '@/hooks/useSEO';

export default function ContactPage() {
  useSEO({
    title: '聯絡我們｜台北萬華手機維修・LINE 快速詢價',
    description: '兄弟維修位於台北市萬華區萬大路251號，週一至週六 13:00–22:00 營業。電話 0916-858-251，或掃描 QR Code 加入 LINE 官方帳號，快速詢問維修報價！',
    keywords: '兄弟維修聯絡,手機維修台北,萬華維修店地址,iPhone維修預約,LINE詢價',
    canonical: '/contact',
  });

  const [pageContent, setPageContent] = useState<Record<string, { title?: string; subtitle?: string }>>({});

  useEffect(() => {
    fetchPageContent().then(content => {
      const ids = ['contact-banner', 'contact-address', 'contact-phone', 'contact-email', 'contact-hours'];
      const map: Record<string, { title?: string; subtitle?: string }> = {};
      ids.forEach(id => {
        const s = content.find(c => c.id === id);
        if (s) map[id] = { title: s.title, subtitle: s.subtitle };
      });
      setPageContent(map);
    });
  }, []);

  const get = (id: string) => pageContent[id] ?? {};

  const contactInfoList = [
    { icon: 'ri-map-pin-line', label: '店面地址', title: get('contact-address').title ?? '台北市萬華區萬大路251號', sub: get('contact-address').subtitle ?? '近萬大路' },
    { icon: 'ri-phone-line', label: '聯絡電話', title: get('contact-phone').title ?? '0916-858-251', sub: get('contact-phone').subtitle ?? '(02) 2305-1647' },
    { icon: 'ri-time-line', label: '營業時間', title: get('contact-hours').title ?? '週一至週六 13:00–22:00', sub: get('contact-hours').subtitle ?? '週日及國定假日休息' },
  ];

  const bannerTitle = get('contact-banner').title ?? '與我們聯絡';
  const bannerSubtitle = get('contact-banner').subtitle ?? '有任何維修問題或需要報價，歡迎透過以下方式聯絡我們';

  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="pt-32 pb-16 bg-gray-950">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <div className="inline-flex items-center gap-2 text-orange-400 text-sm font-medium mb-4">
              <span className="w-8 h-px bg-orange-400" />
              聯絡我們
              <span className="w-8 h-px bg-orange-400" />
            </div>
            <h1 className="text-5xl font-bold text-white mb-4">{bannerTitle}</h1>
            <p className="text-gray-400 max-w-xl mx-auto">{bannerSubtitle}</p>
          </div>
        </section>

        {/* Contact Info */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6">
            <div className="grid sm:grid-cols-3 gap-6 mb-8">
              {contactInfoList.map((c) => (
                <div key={c.label} className="flex items-start gap-4 bg-white rounded-2xl p-6 border border-gray-100">
                  <div className="w-12 h-12 flex items-center justify-center bg-orange-50 rounded-xl flex-shrink-0">
                    <i className={`${c.icon} text-orange-500 text-xl`} />
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 mb-0.5">{c.label}</div>
                    {c.label === '官方 LINE' ? (
                      <a href="https://lin.ee/mOf6eFn" target="_blank" rel="nofollow noopener noreferrer" className="font-semibold text-orange-500 text-sm hover:underline cursor-pointer">{c.title}</a>
                    ) : (
                      <div className="font-semibold text-gray-900 text-sm">{c.title}</div>
                    )}
                    <div className="text-xs text-gray-400 mt-0.5">{c.sub}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* LINE QR Code */}
            <div className="bg-white rounded-2xl border border-gray-100 p-8 mb-8 flex flex-col sm:flex-row items-center gap-8">
              <div className="flex-shrink-0 w-52 h-52">
                <img
                  src="https://storage.readdy-site.link/project_files/a6fca87a-8436-4bd7-9e14-084f599afe41/338c2250-28af-4382-9ccc-b29dd864f9ee_Black-and-Yellow-Food-Promo-Scan-QR-Code-A5-Document.png?v=bcb07a3e6ff57a0cb97d5f0b9de18e8a"
                  alt="官方 LINE QR Code"
                  className="w-full h-full object-contain rounded-xl"
                />
              </div>
              <div>
                <div className="inline-flex items-center gap-2 bg-green-50 text-green-600 text-xs font-medium px-3 py-1 rounded-full mb-3">
                  <i className="ri-line-fill text-sm" />
                  官方 LINE
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">掃碼加入 LINE 官方帳號</h2>
                <p className="text-gray-500 text-sm leading-relaxed mb-4">
                  用手機掃描 QR Code，即可加入我們的 LINE 官方帳號。<br />
                  有任何維修問題、詢價需求，直接透過 LINE 聯絡，最快速方便！
                </p>
                <a
                  href="https://lin.ee/mOf6eFn"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-line-fill" />
                  點此加入 LINE
                </a>
              </div>
            </div>

            {/* Map */}
            <div className="rounded-2xl overflow-hidden h-80 border border-gray-100">
              <iframe
                src="https://maps.google.com/maps?q=台北市萬華區萬大路251號&t=m&z=17&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                title="店面位置"
              />
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
